import { BrowserRouter } from 'react-router-dom'
import { Authenticator } from '@aws-amplify/ui-react'
import { Provider as RollbarProvider } from '@rollbar/react'
import CssBaseline from '@mui/material/CssBaseline'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers'
import CarrotGraphProvider from 'domains/carrotGraph/provider'
import { StreamApiProvider } from 'domains/streamApi/context'
import { mockGraphClient } from 'domains/carrotGraph/mocks/graphClient.mock'
import { createGraphClient } from 'domains/carrotGraph/client'
import { getAuthHeaders } from 'ui/screens/SignIn/helpers'
import ConfigContext from 'ui/contexts/ConfigContext'
import './App.scss'
import AppRoutes from './AppRoutes/AppRoutes'
import { RecentDataAvailabilityContext } from 'ui/contexts'
import { useRecentDataAvailabilityContext } from 'ui/contexts/RecentDataAvailabilityContext'
import useBoundStore from 'domains/zustand/store'

/*
 * @todo: move this into Carrot Graph provider component
 */
const makeGraphClient = (carrotGraph) => {
  if (window.Rune.mockCarrotGraph) {
    return mockGraphClient()
  }

  return createGraphClient({
    ...carrotGraph,
    getHeaders: () => getAuthHeaders(),
  })
}

const initRollbar = (config) => {
  const { environment, accessToken, enabled } = config.vendors.rollbar || {
    enabled: false,
  }
  return {
    accessToken,
    environment,
    enabled,
    captureUncaught: true,
    captureUnhandledRejections: true,
    payload: {
      client: {
        javascript: {
          source_map_enabled: true,
          guess_uncaught_frames: true,
          code_version: process.env.REACT_APP_GIT_SHA,
        },
      },
    },
  }
}

/*


/**
 * The main Carrot application.
 *
 * Wraps the rest of the application with global contexts used throughout the
 * app.
 * @returns {JSX.Element} Application component tree
 */
const App = () => {
  const { config } = window.Rune.Carrot
  const graphClient = makeGraphClient(config.carrotGraph)
  const rollbar = initRollbar(config)

  const setBrowserTimezoneName = useBoundStore(
    (state) => state.setBrowserTimezoneName,
  )

  setBrowserTimezoneName(Intl.DateTimeFormat().resolvedOptions().timeZone)

  const recentDataAvailabilityContextValue = useRecentDataAvailabilityContext()
  return (
    <Authenticator.Provider>
      <RollbarProvider config={rollbar}>
        <ConfigContext.Provider value={config}>
          <CarrotGraphProvider client={graphClient}>
            <BrowserRouter>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <CssBaseline />
                <StreamApiProvider config={config.carrotStream}>
                  <RecentDataAvailabilityContext.Provider
                    value={recentDataAvailabilityContextValue}
                  >
                    <AppRoutes />
                  </RecentDataAvailabilityContext.Provider>
                </StreamApiProvider>
              </LocalizationProvider>
            </BrowserRouter>
          </CarrotGraphProvider>
        </ConfigContext.Provider>
      </RollbarProvider>
    </Authenticator.Provider>
  )
}

export default App
